import {
    doc,
    collection,
    getDoc,
    getDocs,
    setDoc,
    deleteDoc,
} from 'firebase/firestore';
import { CoreFirebaseService } from './CoreFirebaseService';
import type { StepNode } from '@segmently/schema-models/v2';

/**
 * Recursively strip `undefined` values from an object.
 * Firestore rejects documents containing `undefined` at any depth.
 */
function stripUndefined(obj: unknown): unknown {
    if (obj === null || obj === undefined) return null;
    if (Array.isArray(obj)) return obj.map(stripUndefined);
    if (typeof obj === 'object') {
        const result: Record<string, unknown> = {};
        for (const [key, value] of Object.entries(obj as Record<string, unknown>)) {
            if (value !== undefined) {
                result[key] = stripUndefined(value);
            }
        }
        return result;
    }
    return obj;
}

/**
 * StrategyStepNodeService
 *
 * Manages V2 screens (StepNode) within a strategy block.
 * Path: projects/{projectId}/strategies/{strategyId}/blocks/{blockId}/screens/{screenId}
 *
 * Constructor-based path pattern for strategy-block StepNode screens.
 * Uses replaceScreen() with full overwrite (no merge:true) to avoid stale nested key bug.
 */
export class StrategyStepNodeService {
    private readonly projectId: string;
    private readonly strategyId: string;
    private readonly blockId: string;
    private readonly firestore = CoreFirebaseService.getInstance().firestore;

    constructor(projectId: string, strategyId: string, blockId: string) {
        this.projectId = projectId;
        this.strategyId = strategyId;
        this.blockId = blockId;
    }

    /** Collection path for screens subcollection */
    public get collectionPath(): string {
        return `projects/${this.projectId}/strategies/${this.strategyId}/blocks/${this.blockId}/screens`;
    }

    public getProjectId(): string { return this.projectId; }
    public getStrategyId(): string { return this.strategyId; }
    public getBlockId(): string { return this.blockId; }

    private getScreensCollectionRef() {
        return collection(this.firestore, this.collectionPath);
    }

    private getScreenRef(screenId: string) {
        return doc(this.firestore, this.collectionPath, screenId);
    }

    /** Get a specific V2 screen */
    async getScreen(screenId: string): Promise<StepNode | null> {
        try {
            const docRef = this.getScreenRef(screenId);
            const docSnap = await getDoc(docRef);
            if (!docSnap.exists()) return null;
            return { ...docSnap.data(), id: docSnap.id } as StepNode;
        } catch (error) {
            CoreFirebaseService.getInstance().logError(error, 'StrategyStepNodeService.getScreen');
            throw error;
        }
    }

    /** List all V2 screens in this block */
    async listScreens(): Promise<StepNode[]> {
        try {
            const collectionRef = this.getScreensCollectionRef();
            const querySnapshot = await getDocs(collectionRef);
            return querySnapshot.docs.map(d => ({ ...d.data(), id: d.id } as StepNode));
        } catch (error) {
            CoreFirebaseService.getInstance().logError(error, 'StrategyStepNodeService.listScreens');
            throw error;
        }
    }

    /** Add a new V2 screen */
    async addScreen(step: StepNode): Promise<void> {
        try {
            const docRef = this.getScreenRef(step.id);
            const docSnap = await getDoc(docRef);
            if (docSnap.exists()) {
                throw new Error(`StepNode with ID ${step.id} already exists`);
            }
            await setDoc(docRef, stripUndefined(step) as StepNode);
        } catch (error) {
            CoreFirebaseService.getInstance().logError(error, 'StrategyStepNodeService.addScreen');
            throw error;
        }
    }

    /** Replace a V2 screen entirely (full overwrite, no merge) */
    async replaceScreen(screenId: string, step: StepNode): Promise<void> {
        try {
            const docRef = this.getScreenRef(screenId);
            const docSnap = await getDoc(docRef);
            if (!docSnap.exists()) {
                throw new Error(`StepNode with ID ${screenId} not found`);
            }
            await setDoc(docRef, stripUndefined(step) as StepNode);
        } catch (error) {
            CoreFirebaseService.getInstance().logError(error, 'StrategyStepNodeService.replaceScreen');
            throw error;
        }
    }

    /** Delete a V2 screen */
    async deleteScreen(screenId: string): Promise<void> {
        try {
            const docRef = this.getScreenRef(screenId);
            await deleteDoc(docRef);
        } catch (error) {
            CoreFirebaseService.getInstance().logError(error, 'StrategyStepNodeService.deleteScreen');
            throw error;
        }
    }

    /** Copy (duplicate) a V2 screen */
    async copyScreen(sourceScreenId: string, newScreenId?: string): Promise<StepNode> {
        try {
            const sourceDocRef = this.getScreenRef(sourceScreenId);
            const sourceSnap = await getDoc(sourceDocRef);
            if (!sourceSnap.exists()) {
                throw new Error(`Source StepNode ${sourceScreenId} not found`);
            }

            const sourceStep = sourceSnap.data() as StepNode;
            const finalNewScreenId = newScreenId || `${sourceScreenId}_copy_${Date.now()}`;
            const newDocRef = this.getScreenRef(finalNewScreenId);

            const checkSnap = await getDoc(newDocRef);
            if (checkSnap.exists()) {
                throw new Error(`Target StepNode ID ${finalNewScreenId} already exists`);
            }

            const newStep: StepNode = {
                ...sourceStep,
                id: finalNewScreenId,
                name: `${sourceStep.name} (Copy)`,
            };

            await setDoc(newDocRef, stripUndefined(newStep) as StepNode);
            return newStep;
        } catch (error) {
            CoreFirebaseService.getInstance().logError(error, 'StrategyStepNodeService.copyScreen');
            throw error;
        }
    }
}
